import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Alert, AlertColor, AlertTitle, Collapse, IconButton, Stack } from "@mui/material";

import { AccordionKey } from "../utils/accordion-keys/AccordionKeys.types";
import { accordionStorage } from "../utils/accordion-storage/accordionStorage";

interface Props {
  severity: AlertColor;
  title: ReactNode;
  children: ReactNode;
  icon?: ReactNode;
  defaultExpanded?: boolean;
  /** Remembers the expanded state until logout, shared with the persistent accordions. */
  storageKey?: AccordionKey;
}

export function AlertPanelBasic({ severity, title, children, icon, defaultExpanded = true, storageKey }: Props) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<boolean>(
    () => (storageKey ? accordionStorage.get(storageKey) : undefined) ?? defaultExpanded
  );

  const handleToggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (storageKey) {
      accordionStorage.set(storageKey, next);
    }
  };

  return (
    <Alert
      severity={severity}
      icon={false}
      sx={{
        alignItems: "flex-start",
        "& .MuiAlert-message": { flex: 1, textAlign: "left" },
        "& .MuiAlert-action": { pt: 0, mr: 0 },
      }}
      action={
        <IconButton
          color="inherit"
          size="medium"
          aria-label={t(`common.additionalInfo.${expanded ? "collapse" : "expand"}`)}
          aria-expanded={expanded}
          onClick={handleToggle}
        >
          {expanded ? <ExpandLessIcon fontSize="medium" /> : <ExpandMoreIcon fontSize="medium" />}
        </IconButton>
      }
    >
      <AlertTitle sx={{ fontWeight: "bold", display: "flex", alignItems: "center", gap: 1, mb: 0 }}>
        {icon}
        {title}
      </AlertTitle>
      <Collapse in={expanded}>
        <Stack sx={{ mt: 2.5 }}>{children}</Stack>
      </Collapse>
    </Alert>
  );
}
